import React, { Children, cloneElement, isValidElement, useCallback, useEffect, useRef } from 'react';
import { Icon, type IconName } from '../primitives/Icon';
import { dropShadow } from '../theme/shadowStyles';
import { Platform, Pressable, StyleSheet } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '../hooks/useThemeColors';
import { useHaptics } from '../hooks/useHaptics';
import { pushEscapeHandler } from '../utils/escapeStack';
import { ProBadge } from './ProBadge';

/**
 * Speed dial menu primitive — a column of pill-labelled icon rows that
 * stagger in from a trigger (FAB at the bottom, or icon button in the
 * header). Used for the Add, Layers, and Share menus on the trip detail
 * screen.
 *
 * Callers own the trigger button — its styling varies too much across
 * sites (header chrome vs. primary FAB vs. secondary FAB) for a useful
 * single abstraction. Open/close state lives in the caller too, so it
 * can coordinate "open A closes B" between sibling menus.
 *
 * Items are passed as children in top-to-bottom visual order; the
 * wrapper computes each item's stagger index from `direction` so the
 * row nearest the trigger always animates in first.
 */

export type SpeedDialDirection = 'up' | 'down';

export interface SpeedDialAnchor {
  right: number;
  /** Drop-down menus (anchored to a header button) pass `top`. */
  top?: number | undefined;
  /** Drop-up menus (anchored to a bottom FAB) pass `bottom`. */
  bottom?: number | undefined;
}

export interface SpeedDialProps {
  open: boolean;
  onDismiss: () => void;
  anchor: SpeedDialAnchor;
  /** 'up' = items appear above the trigger and stagger from bottom up.
   *  'down' = items appear below the trigger and stagger from top down. */
  direction?: SpeedDialDirection | undefined;
  /** Backdrop tint. Defaults to `theme.overlayLight` — set explicitly
   *  to override (e.g. `overlayDark` for a fully modal-feeling menu). */
  backdropColor?: string | undefined;
  children: React.ReactNode;
}

export function SpeedDial({
  open,
  onDismiss,
  anchor,
  direction = 'up',
  backdropColor,
  children,
}: SpeedDialProps) {
  const theme = useThemeColors();
  // Container ref for the menu — used to scope arrow-key handling
  // and autofocus the first item when opened by keyboard. Per ADR-0077.
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Autofocus the first menu item when the menu opens — keyboard users
  // land inside. Cancellable rAF so a sub-frame close doesn't yank
  // focus back to a menu item that's about to unmount. Effect depends
  // on `open` only so a parent re-render that hands a fresh `onDismiss`
  // closure doesn't re-trigger autofocus mid-navigation.
  useEffect(() => {
    if (!open || Platform.OS !== 'web' || typeof document === 'undefined') return;
    const raf = requestAnimationFrame(() => {
      const items = menuRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]',
      );
      items?.[0]?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Esc closes the menu — keyboard parity with the backdrop-press path.
  // Routed through the shared escape stack so stacked overlays each
  // own their own Escape and a single keypress doesn't cascade. ADR-0077.
  useEffect(() => {
    if (!open) return;
    return pushEscapeHandler(onDismiss);
  }, [open, onDismiss]);

  // Arrow keys cycle within the menu. Uses a document keydown listener
  // because focus may be on individual menuitems whose own onKeyDown
  // would otherwise need plumbing through each child clone.
  useEffect(() => {
    if (!open || Platform.OS !== 'web' || typeof document === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
      const items = menuRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]',
      );
      if (!items?.length) return;
      e.preventDefault();
      const active = document.activeElement as HTMLElement | null;
      const idx = active ? Array.from(items).indexOf(active) : -1;
      const last = items.length - 1;
      let nextIdx = idx;
      if (e.key === 'ArrowDown') nextIdx = idx === last || idx < 0 ? 0 : idx + 1;
      else if (e.key === 'ArrowUp') nextIdx = idx <= 0 ? last : idx - 1;
      else if (e.key === 'Home') nextIdx = 0;
      else if (e.key === 'End') nextIdx = last;
      items[nextIdx]?.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const total = Children.count(children);

  return (
    <>
      <Pressable
        onPress={onDismiss}
        accessibilityLabel="Close menu"
        style={[
          StyleSheet.absoluteFill,
          { backgroundColor: backdropColor ?? theme.overlayLight },
        ]}
      />
      <YStack
        ref={menuRef as never}
        position="absolute"
        right={anchor.right}
        {...(anchor.top !== undefined ? { top: anchor.top } : {})}
        {...(anchor.bottom !== undefined ? { bottom: anchor.bottom } : {})}
        gap={12}
        alignItems="flex-end"
        // Menu container — items below are role=menuitem.
        role="menu"
        accessibilityRole="menu"
      >
        {Children.map(children, (child, index) => {
          if (!isValidElement(child)) return child;
          // Row nearest the trigger gets stagger index 0 so it enters
          // first; rows farther away enter progressively later.
          const staggerIndex = direction === 'up' ? total - 1 - index : index;
          return cloneElement(
            child as React.ReactElement<{ _staggerIndex?: number }>,
            { _staggerIndex: staggerIndex },
          );
        })}
      </YStack>
    </>
  );
}

export interface SpeedDialItemProps {
  icon: IconName;
  label: string;
  onPress: () => void;
  accessibilityLabel?: string | undefined;
  /** Icon tint inside the circle. Defaults to `theme.textOnSurface`. */
  accent?: string | undefined;
  /** Renders the row at 45% opacity — used by toggle-style menus
   *  (e.g. layers) to indicate "off". */
  dimmed?: boolean | undefined;
  /** Shows a `Pro` badge after the label and is the caller's signal to
   *  route taps to the upgrade flow instead of `onPress`. */
  locked?: boolean | undefined;
  /** Optional second line of text inside the pill. */
  description?: string | undefined;
  /** @internal — injected by `<SpeedDial>` to drive entrance stagger.
   *  Pass nothing when using `<SpeedDialItem>` directly. */
  _staggerIndex?: number | undefined;
}

export function SpeedDialItem({
  icon,
  label,
  onPress,
  accessibilityLabel,
  accent,
  dimmed = false,
  locked = false,
  description,
  _staggerIndex = 0,
}: SpeedDialItemProps) {
  const theme = useThemeColors();
  const haptics = useHaptics();
  // ADR-0079: light tap on speed-dial item commit.
  const handlePress = useCallback(() => {
    haptics.light();
    onPress();
  }, [haptics, onPress]);
  return (
    <Animated.View entering={FadeInDown.duration(160).delay(_staggerIndex * 30)}>
      <Pressable
        onPress={handlePress}
        accessibilityRole="menuitem"
        accessibilityLabel={accessibilityLabel ?? label}
        style={({ pressed }) => [
          dimmed && { opacity: 0.45 },
          pressed && { transform: [{ scale: 0.96 }] },
          Platform.OS === 'web' ? ({ cursor: 'pointer' } as object) : null,
        ]}
      >
        <XStack
          alignItems="center"
          gap={10}
          paddingLeft={16}
          paddingRight={6}
          paddingVertical={6}
          borderRadius={28}
          backgroundColor={theme.surface}
          {...dropShadow({ offsetY: 3, radius: 12, opacity: 0.1, color: theme.shadowDefault })}
          elevation={4}
        >
          <YStack alignItems="flex-end">
            <XStack alignItems="center" gap={6}>
              <Text fontSize={13} fontWeight="700" color="$textOnSurface">
                {label}
              </Text>
              {locked ? <ProBadge /> : null}
            </XStack>
            {description ? (
              <Text fontSize={11} color="$textSubdued" marginTop={1}>
                {description}
              </Text>
            ) : null}
          </YStack>
          <YStack
            width={36}
            height={36}
            borderRadius={18}
            backgroundColor={theme.surfaceChip}
            alignItems="center"
            justifyContent="center"
          >
            <Icon name={icon} size={22} color={accent ?? theme.textOnSurface} />
          </YStack>
        </XStack>
      </Pressable>
    </Animated.View>
  );
}
